/**
 * QFresh push notification client.
 *
 * Handles service worker registration, VAPID subscription, permission
 * management, and unsubscribing. All backend calls go through the shared
 * axios client so JWT auth is automatic.
 *
 * Everything here is defensive — older browsers (or iOS Safari without
 * Add-to-Home-Screen) may lack PushManager, Notification, or serviceWorker.
 * Every helper returns a boolean or Promise-of-boolean so the UI can
 * gracefully hide the enable-push affordance when the platform can't
 * support it.
 */
import { pushApi } from "./api";

const SW_URL = "/sw.js";

export const isPushSupported = () => {
  if (typeof window === "undefined") return false;
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
};

/** Chrome/Firefox report "default" until the user answers the prompt. */
export const getPushPermission = () => {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
};

/** Convert a base64url VAPID public key into the Uint8Array PushManager wants. */
const urlBase64ToUint8Array = (base64) => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

/** Register the service worker (idempotent — safe to call on every mount). */
export const registerServiceWorker = async () => {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration(SW_URL);
    if (existing) return existing;
    return await navigator.serviceWorker.register(SW_URL, { scope: "/" });
  } catch (err) {
    console.warn("SW registration failed:", err);
    return null;
  }
};

/**
 * Full subscribe flow: request permission → fetch VAPID key → create
 * PushSubscription → POST to backend. Returns the subscription on success,
 * or throws with a user-friendly message on failure.
 */
export const enablePush = async () => {
  if (!isPushSupported()) {
    throw new Error("Your browser doesn't support push notifications.");
  }

  // 1. Ensure the service worker is registered and ready.
  const reg = await registerServiceWorker();
  if (!reg) throw new Error("Couldn't register service worker.");
  await navigator.serviceWorker.ready;

  // 2. Ask the user for notification permission.
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    throw new Error(
      perm === "denied"
        ? "Notifications are blocked. Enable them in your browser settings and try again."
        : "Permission wasn't granted."
    );
  }

  // 3. Get the VAPID public key from the backend.
  const { public_key: publicKey, configured } = await pushApi.getVapidKey();
  if (!configured || !publicKey) {
    throw new Error("Push isn't configured on the server yet — try again later.");
  }

  // 4. Subscribe (or re-use an existing subscription on this device).
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  // 5. POST the subscription to our backend.
  const raw = sub.toJSON();
  await pushApi.subscribe({
    endpoint: raw.endpoint,
    keys: {
      p256dh: raw.keys.p256dh,
      auth: raw.keys.auth,
    },
    user_agent: (typeof navigator !== "undefined" && navigator.userAgent) || null,
  });
  return sub;
};

/** Cancel the local PushSubscription and tell the backend to forget it. */
export const disablePush = async () => {
  if (!("serviceWorker" in navigator)) return true;
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  if (!reg) return true;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  try {
    await pushApi.unsubscribe({ endpoint });
  } catch (err) {
    // Non-fatal — the server will prune it on next dead-endpoint response.
    console.warn("Backend unsubscribe failed (non-fatal):", err);
  }
  return true;
};

/** True if this device currently has an active PushSubscription. */
export const hasActiveSubscription = async () => {
  if (!("serviceWorker" in navigator)) return false;
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return Boolean(sub);
};
